/* eslint-disable react/jsx-no-useless-fragment */
import { SetStateAction, Dispatch, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  SxProps,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import CustomTextField from "./CustomTextField";
import {
  Category,
  Shop,
  StockLargeClassification,
  Storehouse,
  StorehouseSupplierOptionsWithBlank,
  Supplier,
  TyreOptions,
  WheelOptions,
} from "../types";
import { prefectures } from "../constants";
import { IncrementalSearchElement } from "./Common";
import Surround from "./Surround";
import { extractNoDupplicationSupplier } from "../utils/common";

const incrementalSearchOptionLength = 10;

type Props = {
  open: boolean;
  onClose: Function;
  onSearch: Function;
  productCd: string;
  productName: string;
  brand: string;
  JANCd: string;
  productNumber: string;
  tyreWidth: number | undefined;
  tyreRatio: number | undefined;
  tyreRimDiam: number | undefined;
  wheelWidth: number | undefined;
  wheelPcd: number | undefined;
  wheelHole: number | undefined;
  wheelRimDiam: number | undefined;
  classification: string;
  storehouseSupplier?: StorehouseSupplierOptionsWithBlank;
  storehouse?: string;
  supplier?: string;
  targetShopCdList?: string[];
  targetShopLengthLimit?: number; // 対象店舗の最大選択数
  isHideStoreZero?: boolean; // 店舗用で店舗在庫0で非表示
  isHideStorehouseZero?: boolean; // 店舗用で倉庫在庫0で非表示
  tyreOptions: TyreOptions;
  wheelOptions: WheelOptions;
  classificationOptions: StockLargeClassification[];
  storehouseOptions?: Storehouse[];
  // supplierOptions?: Supplier[];
  brandOptions: string[];
  shopOptions?: Shop[];
  setProductCd: Dispatch<SetStateAction<string>>;
  setProductName: Dispatch<SetStateAction<string>>;
  setBrand: Dispatch<SetStateAction<string>>;
  setJANCd: Dispatch<SetStateAction<string>>;
  setProductNumber: Dispatch<SetStateAction<string>>;
  setTyreWidth: Dispatch<SetStateAction<number | undefined>>;
  setTyreRatio: Dispatch<SetStateAction<number | undefined>>;
  setTyreRimDiam: Dispatch<SetStateAction<number | undefined>>;
  setWheelWidth: Dispatch<SetStateAction<number | undefined>>;
  setWheelPcd: Dispatch<SetStateAction<number | undefined>>;
  setWheelHole: Dispatch<SetStateAction<number | undefined>>;
  setWheelRimDiam: Dispatch<SetStateAction<number | undefined>>;
  setClassification: Dispatch<SetStateAction<string>>;
  setCategory: Dispatch<SetStateAction<Category>>;
  setStorehouseSupplier?: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>;
  setStorehouse?: Dispatch<SetStateAction<string>>;
  setSupplier?: Dispatch<SetStateAction<string>>;
  setTargetShopCdList?: Dispatch<SetStateAction<string[]>>;
  setIsHideStoreZero?: Dispatch<SetStateAction<boolean>>;
  setIsHideStorehouseZero?: Dispatch<SetStateAction<boolean>>;
  isProductStockScreen?: boolean; // 商品管理部在庫一覧でfalse, 商品管理部在庫更新でtrue, その他はundefined
  isSupplierStockScreen?: boolean;
  isCompanyScreen?: boolean;
  isStoreStockScreen?: boolean;
};

const styles: { [key: string]: SxProps } = {
  searchRowBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
};

type SelectFormProps = {
  id: string;
  label: string;
  optionList: string[];
  value: string;
  setValue: Dispatch<SetStateAction<string>> | Function;
  required?: boolean;
  disabled?: boolean;
  isContainBlank?: boolean;
};

/**
 * 選択フォーム
 * @param root0 選択フォームのプロップス
 * @returns JSX.Element
 */
function SelectForm({
  id,
  label,
  optionList,
  value,
  setValue,
  required,
  disabled,
  isContainBlank,
}: SelectFormProps) {
  return (
    <Box width="50%" m={0}>
      <Box p={1}>
        <FormControl sx={{ minWidth: 160, width: "100%", maxWidth: 223 }} required={required}>
          <InputLabel
            id={`${id}-label`}
            sx={{
              top: "-6px",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -4px) scale(0.75)",
              },
              "& .MuiFormLabel-asterisk": {
                color: "var(--red-800)",
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`${id}-label`}
            label={label}
            id={id}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ height: "40px" }}
            disabled={disabled}
          >
            {isContainBlank && (
              // eslint-disable-next-line no-irregular-whitespace, react/self-closing-comp
              <MenuItem key="" value="">
                &emsp;
              </MenuItem>
            )}
            {optionList.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

/**
 * 在庫検索コンポーネント
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
export default function StockSearchDialog({
  open,
  onClose,
  onSearch,
  productCd,
  productName,
  brand,
  JANCd,
  productNumber,
  tyreWidth,
  tyreRatio,
  tyreRimDiam,
  wheelWidth,
  wheelPcd,
  wheelHole,
  wheelRimDiam,
  classification,
  storehouseSupplier,
  storehouse,
  supplier,
  targetShopCdList,
  targetShopLengthLimit,
  isHideStoreZero,
  isHideStorehouseZero,
  tyreOptions,
  wheelOptions,
  classificationOptions,
  storehouseOptions,
  // supplierOptions,
  brandOptions,
  shopOptions,
  setProductCd,
  setProductName,
  setBrand,
  setJANCd,
  setProductNumber,
  setTyreWidth,
  setTyreRatio,
  setTyreRimDiam,
  setWheelWidth,
  setWheelPcd,
  setWheelHole,
  setWheelRimDiam,
  setClassification,
  setCategory,
  setStorehouseSupplier,
  setStorehouse,
  setSupplier,
  setTargetShopCdList,
  setIsHideStoreZero,
  setIsHideStorehouseZero,
  isProductStockScreen,
  isSupplierStockScreen,
  isCompanyScreen,
  isStoreStockScreen,
}: Props) {
  const { showAlert } = useContext(SuccessAlertContext);
  const initValue = () => {
    const backup = {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplier,
      storehouse,
      supplier,
      targetShopCdList,
    };
    return backup;
  };
  const [backup, setBackup] = useState(initValue());
  const resetValue = () => {
    setProductCd(backup.productCd);
    setProductName(backup.productName);
    setBrand(backup.brand);
    setJANCd(backup.JANCd);
    setProductNumber(backup.productNumber);
    setTyreWidth(backup.tyreWidth);
    setTyreRatio(backup.tyreRatio);
    setTyreRimDiam(backup.tyreRimDiam);
    setWheelWidth(backup.wheelWidth);
    setWheelPcd(backup.wheelPcd);
    setWheelHole(backup.wheelHole);
    setWheelRimDiam(backup.wheelRimDiam);
    setClassification(backup.classification);
    if (setStorehouseSupplier && backup.storehouseSupplier != null)
      setStorehouseSupplier(backup.storehouseSupplier);
    if (setStorehouse && backup.storehouse != null) setStorehouse(backup.storehouse);
    if (setSupplier && backup.supplier!= null) setSupplier(backup.supplier);
    // 大分類があればカテゴリも存在する
    if (backup.classification)
      setCategory(
        classificationOptions.filter((co) => co.name === backup.classification)[0].category,
      );
    if (setTargetShopCdList && backup.targetShopCdList)
      setTargetShopCdList(backup.targetShopCdList);
  };
  const handleClose = () => {
    resetValue();
    onClose();
  };

  const handleStorehouseChange = (
    value: string | null,
    options: Storehouse[],
    setSh: Dispatch<SetStateAction<string>>,
    setSup: Dispatch<SetStateAction<string>>,
    setShSup: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>,
  ) => {
    if (value) {
      setSh(options.filter((sh) => sh.name === value)[0].name);
      setSup("");
      setShSup("倉庫");
    } else {
      setSh("");
      setShSup("");
    }
  };
  const handleSupplierChange = (
    value: string | null,
    setSh: Dispatch<SetStateAction<string>>,
    setSup: Dispatch<SetStateAction<string>>,
    setShSup: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>,
  ) => {
    if (value) {
      setSup(value);
      setSh("");
      setShSup("仕入先");
    } else {
      setSup("");
      setShSup("");
    }
  };

  const calcSupplierOptions = () => (
    // let sOptions: string[] = [];
    // if (classification) {
    //   // 分類の中に仕入先のみ候補にする
    //   const sList = classificationOptions.filter((c) => c.name === classification)[0].suppliers;
    //   sOptions = sList ? sList.map((s) => s.supplierName) : [];
    // } else
    //   sOptions = extractNoDupplicationSupplier(classificationOptions).map((so) => so.supplierName); // 全仕入先を候補にする
    // return sOptions;
    extractNoDupplicationSupplier(classificationOptions).map(so => so.supplierName) // 全仕入先を候補にする
  );
  const supplierStorehouseComponent = (required: boolean) =>
    supplier !== undefined &&
    setSupplier &&
    storehouse !== undefined &&
    storehouseOptions &&
    setStorehouse &&
    storehouseSupplier !== undefined &&
    setStorehouseSupplier && ( // 本社用在庫情報一覧、商品管理部用在庫更新画面の場合、この要素が表示される
      <Box sx={styles.searchRowBox}>
        {required && ( // 在庫更新画面へメニューのみ表示
          <>
            <SelectForm key='storehouse' id="storehouse" label="倉庫名" optionList={storehouseOptions.map(sh => sh.name)} value={storehouse} setValue={
              (val: string) => handleStorehouseChange(val, storehouseOptions, setStorehouse, setSupplier, setStorehouseSupplier)
            }/>
          </>
          // <IncrementalSearchElement
          //   label="倉庫名"
          //   options={storehouseOptions.map((sh) => sh.name)}
          //   value={storehouse}
          //   onChange={(val: string) =>
          //     handleStorehouseChange(
          //       val,
          //       storehouseOptions,
          //       setStorehouse,
          //       setSupplier,
          //       setStorehouseSupplier,
          //     )
          //   }
          //   optionLength={incrementalSearchOptionLength}
          // />
        )}
        <IncrementalSearchElement
          label="仕入先名"
          options={calcSupplierOptions()}
          value={supplier}
          onChange={(val: string) =>
            handleSupplierChange(val, setStorehouse, setSupplier, setStorehouseSupplier)
          }
          optionLength={incrementalSearchOptionLength}
        />
      </Box>
    );

  const searchElementRow = () => {
    let searchElements = [
      <Box key="product_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productCd"
          type="text"
          label="商品コード"
          text={productCd}
          setText={setProductCd}
          maxLength={100}
          size="small"
          isNumericText
        />
      </Box>,
      <Box key="jan_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="JANCd"
          type="text"
          label="JANコード"
          text={JANCd}
          setText={setJANCd}
          maxLength={100}
          size="small"
          isNumericText
        />
      </Box>,
      <SelectForm
        key="classification"
        id="classification"
        label="大分類"
        optionList={classificationOptions.map((c) => c.name)}
        value={classification}
        setValue={(val) => {
          if (val === "") {
            setClassification(val);
          } else {
            setCategory(classificationOptions.filter((co) => co.name === val)[0].category);
            setClassification(val);
          }
          // const sups = classificationOptions.filter((c) => c.name === val)[0].suppliers;
          // if (supplier && setSupplier && sups && !sups.some((s) => s.supplierName === supplier)) {
          //   setSupplier("");
          // }
        }}
        required={!isProductStockScreen} isContainBlank
      />,
      <IncrementalSearchElement
        key="brand"
        label="ブランド"
        options={brandOptions}
        value={brand}
        onChange={(val: string) => setBrand(val)}
        optionLength={incrementalSearchOptionLength}
      />,
      <Box key="product_name" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productName"
          type="text"
          label="商品名"
          text={productName}
          setText={setProductName}
          maxLength={100}
          size="small"
        />
      </Box>,
      <Box key="product_number" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productNumber"
          type="text"
          label="品番"
          text={productNumber}
          setText={setProductNumber}
          maxLength={100}
          size="small"
        />
      </Box>,
    ];
    if (
      // 商品部、店舗、企業用、一般店は仕入先名が必要
      !(isProductStockScreen || isSupplierStockScreen) &&
      // extractNoDupplicationSupplier(classificationOptions).length > 0 &&
      supplier !== undefined &&
      setSupplier
    ) {
      searchElements.splice(
        3,
        0,
        <IncrementalSearchElement
          key="supplier_name"
          label="仕入先名"
          options={calcSupplierOptions()}
          value={supplier}
          onChange={(val: string) => setSupplier(val)}
          optionLength={incrementalSearchOptionLength}
          isBlankNoOptions
        />,
      );
    }
    if (isSupplierStockScreen) {
      // 外部倉庫・仕入先のみで使う項目に絞る
      const supplierSearchElementIndices = [0, 1, 4, 5];
      searchElements = searchElements.filter((_, index) =>
        supplierSearchElementIndices.includes(index),
      );
    }
    if (isStoreStockScreen && setIsHideStoreZero && setIsHideStorehouseZero) {
      searchElements.push(
        <FormControlLabel
          sx={{width: "calc(50% - 16px)", maxWidth: 223, mx: 1}}
          control={
            <Checkbox
              color="secondary"
              checked={isHideStoreZero}
              onChange={(e) => setIsHideStoreZero(e.target.checked)}
              inputProps={{
                "aria-label": "is-hide-store-zero",
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 14, color: "var(--black)" }}>
              店舗在庫0を非表示
            </Typography>
          }
        />,
        <FormControlLabel
          sx={{width: "calc(50% - 16px)", maxWidth: 223, mx: 1}}
          control={
            <Checkbox
              color="secondary"
              // indeterminate={selectedOrderList.length < orderList.length}
              checked={isHideStorehouseZero}
              onChange={(e) => setIsHideStorehouseZero(e.target.checked)}
              inputProps={{
                "aria-label": "is-hide-storehouse-zero",
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 14, color: "var(--black)" }}>
              倉庫在庫0を非表示
            </Typography>
          }
        />,
      );
    }
    const len = Math.ceil(searchElements.length / 2);
    const couples: JSX.Element[] = [];
    for (let i = 0; i < len; i += 1) {
      const element1 = searchElements[i * 2];
      let element2: JSX.Element | undefined;
      if (i * 2 + 1 < searchElements.length) {
        element2 = searchElements[i * 2 + 1];
      }
      couples.push(
        <Box sx={styles.searchRowBox}>
          {element1}
          {element2 ?? <></>}
        </Box>,
      );
    }
    return <>{couples}</>;
  };

  const [selectedPrefectureIndex, setSelectedPrefectureIndex] = useState(0);
  // チェックボックス押下時の処理
  const handleCheck = (
    shopCdList: string[],
    checked: boolean,
    sTargetShopCdList: Dispatch<SetStateAction<string[]>>,
    tarShopLengthLimit: number,
    tarShopCdList: string[],
    sOptions: Shop[],
  ) => {
    sTargetShopCdList((prev) => {
      if (prev.length >= tarShopLengthLimit && checked) {
        showAlert(`対象店舗は最大${targetShopLengthLimit}店舗まで選択可能です。`, "error");
        return prev;
      }
      // 全選択の場合、上限値までのみチェックする
      const shopCds = checked
        ? (() => {
            // 現在選択済みの店舗を候補から除去
            const sCds = shopCdList.filter((sCd) => !prev.includes(sCd));
            // 残りの数だけ選択する
            return sCds.slice(0, tarShopLengthLimit - tarShopCdList.length);
          })()
        : shopCdList;

      let newList = [...prev];
      shopCds.forEach((shopCd) => {
        if (checked) newList.push(shopCd);
        else newList = newList.filter((sCd) => sCd !== shopCd);
      });
      // 枠内全選択の場合に重複を除去
      const noDuplicateList = Array.from(new Set(newList));
      // shopOptions順にソートする
      const sortedList: string[] = [];
      sOptions.forEach((so) => {
        if (noDuplicateList.includes(so.shopCd)) sortedList.push(so.shopCd);
      });
      return sortedList;
    });
  };
  const searchCheckboxElement = () => {
    const checkboxElements: JSX.Element[] = [];

    // 企業用で使用
    if (
      isCompanyScreen &&
      shopOptions &&
      shopOptions.length > 0 &&
      setTargetShopCdList &&
      targetShopCdList &&
      targetShopLengthLimit
    ) {
      const targetShopOptions =
        selectedPrefectureIndex === 0
          ? shopOptions
          : shopOptions.filter((so) => so.prefecture === selectedPrefectureIndex);

      // 上限値選択されているか、絞り込んだチェックボックスが全て選択されているか
      const checkCheckedTargetShopOptionsAll = () =>
        targetShopCdList.length >= targetShopLengthLimit ||
        targetShopOptions.length ===
          targetShopOptions.filter((tso) => targetShopCdList.includes(tso.shopCd)).length;

      checkboxElements.push(
        <Box mt={2}>
          <Surround
            titleComponent={
              <Box
                position="absolute"
                top={-15}
                left={8}
                px={1}
                sx={{ backgroundColor: "var(--white)" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      checked={checkCheckedTargetShopOptionsAll()}
                      onChange={(event, checked) =>
                        handleCheck(
                          Array.from(
                            { length: targetShopOptions.length },
                            (_, index) => targetShopOptions[index].shopCd,
                          ),
                          checked,
                          setTargetShopCdList,
                          targetShopLengthLimit,
                          targetShopCdList,
                          shopOptions,
                        )
                      }
                      inputProps={{
                        "aria-label": "all-selected",
                      }}
                      sx={{ py: 0, pr: 0.5 }}
                    />
                  }
                  label={
                    !checkCheckedTargetShopOptionsAll()
                      ? `枠内${Math.min(targetShopLengthLimit - targetShopCdList.length, targetShopOptions.filter((tso) => !targetShopCdList.includes(tso.shopCd)).length)}件まとめて選択`
                      : "枠内全解除"
                  }
                  sx={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                />
              </Box>
            }
          >
            <Box px={2} py={1} maxWidth={200} display="inline-flex" flexDirection="column" mt={2}>
              {targetShopOptions.map((so) => {
                const checked = targetShopCdList.includes(so.shopCd);
                return (
                  <FormControlLabel
                    key={so.shopCd}
                    control={
                      <Checkbox
                        color="secondary"
                        checked={checked}
                        onChange={(_, check) =>
                          handleCheck(
                            [so.shopCd],
                            check,
                            setTargetShopCdList,
                            targetShopLengthLimit,
                            targetShopCdList,
                            shopOptions,
                          )
                        }
                        inputProps={{
                          "aria-label": "all-selected",
                        }}
                      />
                    }
                    sx={{
                      "& .MuiFormControlLabel-root": {
                        wordBreak: "break-all"
                      }
                    }}
                    label={so.shopName}
                  />
                );
              })}
            </Box>
          </Surround>
        </Box>,
      );

      checkboxElements.push(
        <Box mt={2} ml={3} minWidth={250}>
          <Surround
            titleComponent={
              <Box
                position="absolute"
                top={-14}
                left={8}
                px={1}
                sx={{ backgroundColor: "var(--white)" }}
              >
                <Box component="span" fontSize={14} mr={1}>
                  選択中店舗
                </Box>
                <Button
                  variant="custom-outlined"
                  sx={{ borderRadius: 2, m: 0, px: 1, py: 0, fontSize: 14 }}
                  onClick={() => setTargetShopCdList([])}
                >
                  全店舗クリア
                </Button>
              </Box>
            }
          >
            <Box display="flex" flexDirection="row" flexWrap="wrap" mt={2} maxWidth={450}>
              {targetShopCdList.length > 0 ? (
                targetShopCdList.map((tsCd) => {
                  const sName = shopOptions.filter((s) => s.shopCd === tsCd)[0].shopName;
                  return (
                    <Button
                      key={tsCd}
                      variant="custom-outlined"
                      sx={{ borderRadius: 2, m: 1, px: 1, py: 0, fontSize: 14 }}
                      onClick={() =>
                        handleCheck(
                          [tsCd],
                          false,
                          setTargetShopCdList,
                          targetShopLengthLimit,
                          targetShopCdList,
                          shopOptions,
                        )
                      }
                    >
                      {sName}
                      <Box>×</Box>
                    </Button>
                  );
                })
              ) : (
                <Box m={1.2}>選択なし</Box>
              )}
            </Box>
          </Surround>
        </Box>,
      );
    }
    return (
      <Box mt={2}>
        <Surround title="対象店舗">
          <Box p={1.5}>
            <SelectForm
              id="search-shop-by-prefecture"
              label="都道府県を選択"
              optionList={
                shopOptions
                  ? [
                      prefectures[0],
                      ...prefectures.filter((_, i) =>
                        shopOptions.some((so) => so.prefecture === i),
                      ),
                    ]
                  : prefectures
              }
              value={prefectures[selectedPrefectureIndex]}
              setValue={(val: string) => {
                setSelectedPrefectureIndex(prefectures.indexOf(val));
              }}
            />
            <Box display="flex" flexDirection="row">
              {checkboxElements}
            </Box>
          </Box>
        </Surround>
      </Box>
    );
  };

  useEffect(() => {
    if (open) {
      // 開いたときにバックアップを作成
      setBackup(initValue());
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => handleClose()} PaperProps={isCompanyScreen ? {sx: {width: 800, maxWidth: 800}} : {}}>
      <Box p={2}>
        <DialogTitle>絞り込み検索</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {isProductStockScreen && supplierStorehouseComponent(true)}
          {searchElementRow()}
          {!isSupplierStockScreen && (
            <>
              {classification === "タイヤ" && (
                <Surround title="タイヤ用">
                  <Box marginTop={0.5} />
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="tyre-width"
                      label="幅"
                      optionList={tyreOptions.tyreWidthList.map((t) => `${t}`)}
                      value={`${tyreWidth ?? ""}`}
                      setValue={(val) => {
                        setTyreWidth(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="tyre-ratio"
                      label="偏平率"
                      optionList={tyreOptions.tyreRatioList.map((t) => `${t}`)}
                      value={`${tyreRatio ?? ""}`}
                      setValue={(val) => {
                        setTyreRatio(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="tyre-rim-diam"
                      label="タイヤリム径"
                      optionList={tyreOptions.tyreRimDiamList.map((t) => `${t}`)}
                      value={`${tyreRimDiam ?? ""}`}
                      setValue={(val) => {
                        setTyreRimDiam(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                </Surround>
              )}
              {classification === "ホイール" && (
                <Surround title="ホイール用">
                  <Box marginTop={0.5} />
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="wheel-rim-diam"
                      label="ホイールリム径"
                      optionList={wheelOptions.wheelRimDiamList.map((t) => `${t}`)}
                      value={`${wheelRimDiam ?? ""}`}
                      setValue={(val) => {
                        setWheelRimDiam(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="wheel-width"
                      label="リム幅"
                      optionList={wheelOptions.wheelWidthList.map((t) => `${t}`)}
                      value={`${wheelWidth ?? ""}`}
                      setValue={(val) => {
                        setWheelWidth(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="wheel-hole"
                      label="ホール数"
                      optionList={wheelOptions.wheelHoleList.map((t) => `${t}`)}
                      value={`${wheelHole ?? ""}`}
                      setValue={(val) => {
                        setWheelHole(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="wheel-pcd"
                      label="PCD"
                      optionList={wheelOptions.wheelPcdList.map((t) => `${t.toFixed(2)}`)}
                      value={`${wheelPcd ? wheelPcd.toFixed(2) : ""}`}
                      setValue={(val) => {
                        setWheelPcd(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                </Surround>
              )}
            </>
          )}
          {isCompanyScreen && searchCheckboxElement()}
        </DialogContent>
        <DialogActions>
          <Button variant="custom-outlined" onClick={() => handleClose()}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // 大分類は必須。本部用在庫更新は必須ではない
              if ((!isSupplierStockScreen && !isProductStockScreen)  && classification.length === 0) {
                showAlert("大分類を設定して下さい。", "error");
                return;
              }
              if (isCompanyScreen && targetShopCdList && targetShopCdList.length === 0) {
                showAlert("対象店舗を最低1つは選択してください。", "error");
                return;
              }
              onSearch();
            }}
          >
            検索
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
